import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDoOrDrinkStore } from '../stores/doOrDrinkStore';

function DoOrDrinkGame() {
  const navigate = useNavigate();
  const phase = useDoOrDrinkStore((s) => s.phase);
  const players = useDoOrDrinkStore((s) => s.players);
  const currentPlayerIndex = useDoOrDrinkStore((s) => s.currentPlayerIndex);
  const currentDare = useDoOrDrinkStore((s) => s.currentDare);
  const config = useDoOrDrinkStore((s) => s.config);
  const roundNumber = useDoOrDrinkStore((s) => s.roundNumber);
  const lastAction = useDoOrDrinkStore((s) => s.lastAction);
  const dareCompleted = useDoOrDrinkStore((s) => s.dareCompleted);
  const dareSkipped = useDoOrDrinkStore((s) => s.dareSkipped);
  const advancePlayer = useDoOrDrinkStore((s) => s.advancePlayer);
  const resetGame = useDoOrDrinkStore((s) => s.resetGame);

  // Redirect to setup if no game is running
  useEffect(() => {
    if (phase === 'setup' || players.length === 0) {
      navigate('/doOrDrink');
    }
  }, [phase, players.length, navigate]);

  // Auto-advance from result phase after 1.5 seconds
  useEffect(() => {
    if (phase === 'result') {
      const timer = setTimeout(() => {
        advancePlayer();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [phase, advancePlayer]);

  const handlePlayAgain = useCallback(() => {
    resetGame();
    navigate('/doOrDrink');
  }, [resetGame, navigate]);

  const handleHome = useCallback(() => {
    resetGame();
    navigate('/');
  }, [resetGame, navigate]);

  if (players.length === 0) {
    return null;
  }

  const currentPlayer = players[currentPlayerIndex]!;

  // Find winner (player with most dares completed at game end)
  const winner = phase === 'game_end'
    ? [...players].sort((a, b) => b.daresCompleted - a.daresCompleted)[0]
    : null;

  const sortedPlayers = [...players].sort((a, b) => b.daresCompleted - a.daresCompleted);

  // Playing phase
  if (phase === 'playing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-base-100 px-4">
        <div className="max-w-md w-full text-center space-y-8">
          {/* Round info */}
          <div className="flex justify-between items-center text-base-content/50 text-sm">
            <span>Round {roundNumber}</span>
            <span>
              {currentPlayer.daresCompleted}/{config.targetDares} dares
            </span>
          </div>

          {/* Current player */}
          <div>
            <p className="text-base-content/60 text-sm mb-1">Pass the phone to</p>
            <h2 className="text-4xl font-black text-primary">{currentPlayer.name}</h2>
          </div>

          {/* Dare card */}
          <div className="card bg-base-200">
            <div className="card-body">
              <p className="text-xl font-medium leading-relaxed">{currentDare}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={dareSkipped}
              className="btn btn-outline btn-lg flex-1 text-lg"
            >
              I'll Drink
            </button>
            <button
              onClick={dareCompleted}
              className="btn btn-primary btn-lg flex-1 text-lg"
            >
              I'll Do It
            </button>
          </div>

          {/* Mini scoreboard */}
          <div className="flex flex-wrap justify-center gap-2">
            {players.map((p, i) => (
              <div
                key={i}
                className={`badge ${
                  i === currentPlayerIndex ? 'badge-primary' : 'badge-neutral'
                }`}
              >
                {p.name}: {p.daresCompleted}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Result phase (brief animation)
  if (phase === 'result') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-base-100 px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <h2 className="text-3xl font-bold text-primary">{currentPlayer.name}</h2>
          {lastAction === 'completed' ? (
            <div className="space-y-3">
              <div className="text-6xl">&#128170;</div>
              <p className="text-2xl font-bold text-success">Did it!</p>
              <p className="text-base-content/60">
                {currentPlayer.daresCompleted} / {config.targetDares} dares completed
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-6xl">&#127866;</div>
              <p className="text-2xl font-bold text-warning">Drink up!</p>
              <p className="text-base-content/60">
                {currentPlayer.daresDrunk} drink{currentPlayer.daresDrunk !== 1 ? 's' : ''} total
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game end phase
  if (phase === 'game_end' && winner) {
    const totalDares = players.reduce((sum, p) => sum + p.daresCompleted, 0);
    const totalDrinks = players.reduce((sum, p) => sum + p.daresDrunk, 0);

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-base-100 px-4 py-8">
        <div className="max-w-md w-full text-center space-y-6">
          <div>
            <p className="text-base-content/60 text-sm mb-2">Winner!</p>
            <h2 className="text-5xl font-black text-primary">{winner.name}</h2>
            <p className="text-base-content/60 mt-2">
              Completed {winner.daresCompleted} dares
            </p>
          </div>

          {/* Scoreboard */}
          <div className="card bg-base-200">
            <div className="card-body">
              <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-3">
                Scoreboard
              </h3>
              <div className="space-y-2">
                {sortedPlayers.map((p, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center p-2 rounded-lg ${
                      p.name === winner.name ? 'bg-primary/10' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base-content/40 w-6">{i + 1}.</span>
                      <span className={`font-medium ${p.name === winner.name ? 'text-primary' : ''}`}>
                        {p.name}
                      </span>
                    </div>
                    <div className="flex gap-3 text-sm">
                      <span className="text-success">{p.daresCompleted} done</span>
                      <span className="text-warning">{p.daresDrunk} drunk</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="card bg-base-200">
              <div className="card-body py-3 px-2">
                <p className="text-2xl font-bold">{roundNumber}</p>
                <p className="text-xs text-base-content/50">Rounds</p>
              </div>
            </div>
            <div className="card bg-base-200">
              <div className="card-body py-3 px-2">
                <p className="text-2xl font-bold text-success">{totalDares}</p>
                <p className="text-xs text-base-content/50">Dares Done</p>
              </div>
            </div>
            <div className="card bg-base-200">
              <div className="card-body py-3 px-2">
                <p className="text-2xl font-bold text-warning">{totalDrinks}</p>
                <p className="text-xs text-base-content/50">Drinks</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={handleHome} className="btn btn-ghost flex-1">
              Home
            </button>
            <button onClick={handlePlayAgain} className="btn btn-primary flex-1">
              Play Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default DoOrDrinkGame;
